import React, { useState } from 'react';
import AdminSidebar from '../../Sidebar/AdminSidebar';
import AdminNavbar from '../../Navbars/AdminNavbar';
import { NavLink } from 'react-router-dom';
import {
    BookOpen, Copy, Check, ChevronDown, ChevronRight, Link2,
    AlertTriangle, HelpCircle, Code2, Send, UserCheck,
} from 'lucide-react';

/**
 * Plain-English manual for the API gateway, written for admins and the product
 * owner rather than developers. The technical endpoint reference is still here
 * but collapsed at the bottom, so the page opens on the part most people need:
 * how to get a merchant or partner live, and which links to send them.
 *
 * Endpoint paths were read off `php artisan route:list` for routes/api_v1.php.
 */

const API_HOST = 'https://www.api.skynetworldwide.com.ng';
const API_BASE = `${API_HOST}/api/v1`;
const APP_HOST = 'https://skynetworldwide.com.ng';

// ── Links you hand out ──────────────────────────────────────────
const SHARE_LINKS = [
    {
        label: 'Sign-up page',
        url: `${APP_HOST}/api-access/register`,
        who: 'Send to a merchant who wants to connect their system',
        why: 'They fill in their company details and it arrives here for approval.',
    },
    {
        label: 'Login page',
        url: `${APP_HOST}/api-access/login`,
        who: 'Send once you have approved them',
        why: 'Where they sign in to collect their key and see their usage.',
    },
    {
        label: 'Their portal',
        url: `${APP_HOST}/api-portal/dashboard`,
        who: 'Where they land after logging in',
        why: 'Their key, usage, wallet, invoices and webhooks all live here.',
    },
    {
        label: 'Technical guide for their developer',
        url: `${API_BASE}/docs`,
        who: 'Send to whoever writes their code',
        why: 'Full instructions with a try-it-out button. Nothing for you to explain.',
    },
    {
        label: 'Connection address',
        url: API_BASE,
        who: 'Their developer needs this',
        why: 'The address their system sends bookings to.',
    },
];

// ── Onboarding journeys ─────────────────────────────────────────
type Step = { actor: 'you' | 'them' | 'system'; title: string; detail: string; link?: { label: string; to: string; external?: boolean } };

const MERCHANT_STEPS: Step[] = [
    {
        actor: 'you',
        title: 'Send them the sign-up link',
        detail: 'Email the merchant the sign-up page. Nothing needs setting up beforehand.',
        link: { label: `${APP_HOST}/api-access/register`, to: `${APP_HOST}/api-access/register`, external: true },
    },
    {
        actor: 'them',
        title: 'They fill in the form',
        detail: 'Company name, contact person, email, phone, what they do, and roughly how many shipments a month they expect. They also choose a password.',
    },
    {
        actor: 'system',
        title: 'The request arrives as "pending"',
        detail: 'Nothing works for them yet. They cannot book anything until you approve.',
    },
    {
        actor: 'you',
        title: 'Review and approve',
        detail: 'Open Portal Accounts — pending ones are listed first. Check the company is who they say they are, then approve. Reject if not, and they are told why.',
        link: { label: 'Open Portal Accounts', to: '/admin/api-gateway/portal-accounts' },
    },
    {
        actor: 'system',
        title: 'Their account and key are created automatically',
        detail: 'Approving creates their account, generates their secret key, and emails them. You do not have to create a key by hand.',
    },
    {
        actor: 'them',
        title: 'They log in and copy their key',
        detail: 'They sign in, open API Keys in their portal and reveal the key. It is shown to them, not to you — if they lose it they regenerate it themselves.',
        link: { label: `${APP_HOST}/api-access/login`, to: `${APP_HOST}/api-access/login`, external: true },
    },
    {
        actor: 'you',
        title: 'Agree the commercials (only if they pay on account)',
        detail: 'If they are billed monthly rather than per booking, set their rates and top up their wallet on their partner record. Skip this for pay-as-you-go.',
        link: { label: 'Open Partner Management', to: '/admin/api-gateway/partners' },
    },
    {
        actor: 'them',
        title: 'Their developer connects',
        detail: 'They point their system at the connection address and start booking. Send their developer the technical guide — you do not need to walk them through it.',
        link: { label: 'Technical guide', to: `${API_BASE}/docs`, external: true },
    },
    {
        actor: 'you',
        title: 'Check it is working',
        detail: 'Their first bookings appear on the Analytics page within minutes. If nothing shows up after a day, see the troubleshooting section below.',
        link: { label: 'Open Analytics', to: '/admin/api-gateway/analytics' },
    },
];

const PARTNER_STEPS: Step[] = [
    {
        actor: 'you',
        title: 'Create the partner yourself',
        detail: 'Large partners such as NIPOST do not sign up online — you add them. Open Partner Management and create the record with their company details.',
        link: { label: 'Open Partner Management', to: '/admin/api-gateway/partners' },
    },
    {
        actor: 'you',
        title: 'Approve them',
        detail: 'A partner stays inactive until approved. Nothing they send will be accepted before this.',
    },
    {
        actor: 'you',
        title: 'Set what they pay',
        detail: 'Add a rate agreement and, if applicable, a markup. This decides what their shipments cost. Get it right before they start — it applies to bookings from that point on.',
    },
    {
        actor: 'you',
        title: 'Issue their key',
        detail: 'Create an API key for them under API Keys. The secret is shown once and never again — copy it somewhere safe and send it to them securely. If it goes missing, rotate it and send a new one.',
        link: { label: 'Open API Keys', to: '/admin/api-gateway/api-keys' },
    },
    {
        actor: 'you',
        title: 'Fund their wallet or set terms',
        detail: 'Partners booking on account need a funded wallet. If it runs empty their bookings are refused, so watch the balance.',
    },
    {
        actor: 'them',
        title: 'Their developer connects',
        detail: 'Send them the connection address and the technical guide. Their first booking will show on Analytics.',
        link: { label: 'Technical guide', to: `${API_BASE}/docs`, external: true },
    },
];

// ── Plain-English glossary ──────────────────────────────────────
const GLOSSARY = [
    { term: 'API', means: 'A way for another company\'s software to talk to ours directly, with nobody logging in or typing anything.' },
    { term: 'API key', means: 'A long secret password for their software. It identifies them on every booking. Treat it like a password — if it leaks, rotate it.' },
    { term: 'Connection address (base URL)', means: `The web address their system sends bookings to: ${API_BASE}` },
    { term: 'Merchant / customer', means: 'An ordinary Skynet customer who wants to book from their own website or system. They sign up online and you approve them.' },
    { term: 'Partner', means: 'A larger company such as NIPOST that hands us shipments in volume, usually billed on account. You create these yourself.' },
    { term: 'Portal', means: 'The small self-service website where a merchant sees their key, usage and invoices. They log in, not you.' },
    { term: 'Webhook', means: 'The reverse direction: we automatically notify their system when a shipment status changes, so they do not have to keep asking.' },
    { term: 'Rate limit', means: 'A cap on how many requests a minute they may send, so one integrator cannot overwhelm the system. Exceeding it gives them a "429" error.' },
    { term: 'Wallet', means: 'Their prepaid balance for shipments booked on account. Empty wallet means bookings are refused.' },
];

// ── Troubleshooting in plain terms ──────────────────────────────
const TROUBLESHOOTING = [
    {
        symptom: 'They say their key "is not authorised" or they get a 401 or 403 error',
        cause: 'Usually the account is not approved yet, or the key was revoked.',
        fix: 'Check their record in Portal Accounts is active, and their key in API Keys is not revoked or expired.',
    },
    {
        symptom: 'They get a "429" or say they are being blocked',
        cause: 'They are sending more requests per minute than their limit allows.',
        fix: 'Their limit can be raised on their key. Ask your developer if you are unsure what to set.',
    },
    {
        symptom: 'Bookings are refused for "insufficient balance"',
        cause: 'Their wallet is empty and they book on account.',
        fix: 'Top up their wallet on their partner record, or take payment first.',
    },
    {
        symptom: 'Shipments are created but the status never changes',
        cause: 'The background job that syncs statuses is not running on the server.',
        fix: 'This one is for your developer — the scheduled jobs listed in the developer section need to be running.',
    },
    {
        symptom: 'They say they are not receiving notifications',
        cause: 'Their webhook address is wrong or their system was down when we tried.',
        fix: 'Ask them to send a test notification from their portal — it shows immediately whether their address works.',
    },
];

// ── Developer reference (collapsed) ─────────────────────────────
type Endpoint = { method: string; path: string; note?: string };

const SURFACES = [
    {
        id: 'partner', name: 'Partner API', prefix: '/partner', auth: 'X-API-Key (partner key)',
        endpoints: [
            { method: 'POST', path: '/rates/quote', note: 'Price before booking' },
            { method: 'POST', path: '/shipments', note: 'Create a shipment' },
            { method: 'GET', path: '/shipments' },
            { method: 'GET', path: '/shipments/{identifier}' },
            { method: 'GET', path: '/shipments/{identifier}/track' },
            { method: 'POST', path: '/shipments/{identifier}/waybill' },
            { method: 'POST', path: '/pickups', note: 'Schedule a pickup' },
            { method: 'GET', path: '/account/profile' },
            { method: 'GET', path: '/account/balance' },
            { method: 'GET', path: '/account/transactions' },
            { method: 'GET', path: '/webhooks' },
            { method: 'POST', path: '/webhooks' },
            { method: 'PUT', path: '/webhooks/{id}' },
            { method: 'DELETE', path: '/webhooks/{id}' },
            { method: 'POST', path: '/webhooks/{id}/test' },
        ] as Endpoint[],
    },
    {
        id: 'customer', name: 'Customer API', prefix: '/customer', auth: 'X-API-Key (customer key)',
        endpoints: [
            { method: 'POST', path: '/rates/calculate' },
            { method: 'POST', path: '/shipments' },
            { method: 'POST', path: '/shipments/bulk' },
            { method: 'POST', path: '/shipments/validate' },
            { method: 'GET', path: '/shipments' },
            { method: 'GET', path: '/shipments/{shipmentNumber}' },
            { method: 'DELETE', path: '/shipments/{shipmentNumber}' },
            { method: 'GET', path: '/shipments/{shipmentNumber}/track' },
            { method: 'POST', path: '/shipments/{shipmentNumber}/waybill' },
            { method: 'GET', path: '/templates/shipment' },
            { method: 'GET', path: '/analytics/dashboard' },
            { method: 'GET', path: '/account/profile' },
            { method: 'GET', path: '/account/balance' },
            { method: 'GET', path: '/account/transactions' },
            { method: 'GET', path: '/webhooks' },
            { method: 'POST', path: '/webhooks' },
            { method: 'PUT', path: '/webhooks/{id}' },
            { method: 'DELETE', path: '/webhooks/{id}' },
            { method: 'POST', path: '/webhooks/{id}/test' },
        ] as Endpoint[],
    },
    {
        id: 'portal', name: 'Portal (merchant self-service)', prefix: '/portal', auth: 'Bearer token from /portal/login',
        endpoints: [
            { method: 'POST', path: '/register', note: 'Public' },
            { method: 'POST', path: '/login', note: 'Public' },
            { method: 'POST', path: '/forgot-password', note: 'Public' },
            { method: 'POST', path: '/logout' },
            { method: 'GET', path: '/dashboard' },
            { method: 'GET', path: '/profile' },
            { method: 'PUT', path: '/profile' },
            { method: 'GET', path: '/api-keys' },
            { method: 'GET', path: '/api-keys/{id}/reveal' },
            { method: 'POST', path: '/api-keys/{id}/regenerate' },
            { method: 'POST', path: '/api-keys/{keyId}/request-rotation' },
            { method: 'GET', path: '/usage' },
            { method: 'GET', path: '/usage/daily' },
            { method: 'GET', path: '/wallet' },
            { method: 'GET', path: '/wallet/transactions' },
            { method: 'GET', path: '/invoices' },
            { method: 'GET', path: '/invoices/{id}' },
            { method: 'GET', path: '/invoices/{id}/download' },
            { method: 'GET', path: '/webhooks' },
            { method: 'POST', path: '/webhooks' },
            { method: 'PUT', path: '/webhooks/{id}' },
            { method: 'DELETE', path: '/webhooks/{id}' },
            { method: 'POST', path: '/webhooks/{id}/test' },
        ] as Endpoint[],
    },
    {
        id: 'admin', name: 'Admin API (what these screens call)', prefix: '/admin', auth: 'X-Admin-Token',
        endpoints: [
            { method: 'GET', path: '/partners' },
            { method: 'POST', path: '/partners' },
            { method: 'GET', path: '/partners/{id}' },
            { method: 'PUT', path: '/partners/{id}' },
            { method: 'POST', path: '/partners/{id}/approve' },
            { method: 'POST', path: '/partners/{id}/reject' },
            { method: 'POST', path: '/partners/{id}/suspend' },
            { method: 'POST', path: '/partners/{id}/reactivate' },
            { method: 'POST', path: '/partners/{id}/markup' },
            { method: 'POST', path: '/partners/{id}/wallet/credit' },
            { method: 'POST', path: '/partners/{id}/wallet/debit' },
            { method: 'GET', path: '/partners/{partnerId}/rate-agreements' },
            { method: 'POST', path: '/partners/{partnerId}/rate-agreements' },
            { method: 'PUT', path: '/rate-agreements/{id}' },
            { method: 'DELETE', path: '/rate-agreements/{id}' },
            { method: 'GET', path: '/partners/{partnerId}/invoices' },
            { method: 'POST', path: '/partners/{partnerId}/invoices/generate' },
            { method: 'GET', path: '/invoices' },
            { method: 'GET', path: '/invoices/{id}' },
            { method: 'PUT', path: '/invoices/{id}/status' },
            { method: 'GET', path: '/api-keys' },
            { method: 'POST', path: '/api-keys' },
            { method: 'GET', path: '/api-keys/{id}' },
            { method: 'POST', path: '/api-keys/{id}/rotate' },
            { method: 'POST', path: '/api-keys/{id}/revoke' },
            { method: 'GET', path: '/portal-accounts' },
            { method: 'GET', path: '/portal-accounts/{id}' },
            { method: 'POST', path: '/portal-accounts/{id}/approve' },
            { method: 'POST', path: '/portal-accounts/{id}/reject' },
            { method: 'DELETE', path: '/portal-accounts/{id}' },
            { method: 'POST', path: '/customer-api-access/{userId}/approve' },
            { method: 'POST', path: '/customer-api-access/{userId}/reject' },
            { method: 'GET', path: '/analytics/overview' },
            { method: 'GET', path: '/analytics/requests' },
            { method: 'GET', path: '/analytics/errors' },
            { method: 'GET', path: '/analytics/top-consumers' },
            { method: 'GET', path: '/system/health' },
            { method: 'GET', path: '/system/stats' },
        ] as Endpoint[],
    },
];

const CRON_JOBS = [
    '/cron/run-all — runs everything below; this is the one to schedule',
    '/cron/sync-nipost-statuses — pulls status changes back from NIPOST',
    '/cron/retry-failed-webhooks',
    '/cron/disable-failing-webhooks',
    '/cron/cleanup-webhook-deliveries',
    '/cron/generate-partner-invoices',
    '/cron/mark-overdue-invoices',
    '/cron/expire-api-keys',
    '/cron/expire-rate-limits',
    '/cron/cleanup-logs',
];

const methodColour = (m: string) =>
    m === 'GET' ? 'bg-green-100 text-green-700 border-green-200'
        : m === 'POST' ? 'bg-blue-100 text-blue-700 border-blue-200'
            : m === 'PUT' ? 'bg-amber-100 text-amber-700 border-amber-200'
                : 'bg-red-100 text-red-700 border-red-200';

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button type="button" title="Copy"
            onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="text-[#808080] hover:text-[#E04343] shrink-0">
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
}

function ActorChip({ actor }: { actor: Step['actor'] }) {
    const map = {
        you: { label: 'You do this', cls: 'bg-[#E04343] text-white' },
        them: { label: 'They do this', cls: 'bg-blue-100 text-blue-800' },
        system: { label: 'Automatic', cls: 'bg-gray-100 text-gray-600' },
    } as const;
    return <span className={`text-[10px] font-[700] px-2 py-0.5 rounded-full shrink-0 ${map[actor].cls}`}>{map[actor].label}</span>;
}

function Journey({ steps }: { steps: Step[] }) {
    return (
        <div className="space-y-2">
            {steps.map((s, i) => (
                <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                        <span className="w-7 h-7 rounded-full bg-[#19213D] text-white text-[12px] font-[700] flex items-center justify-center shrink-0">
                            {i + 1}
                        </span>
                        {i < steps.length - 1 && <span className="flex-1 w-px bg-gray-300 my-1" />}
                    </div>
                    <div className="flex-1 pb-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-[13.5px] font-[600] text-[#19213D]">{s.title}</p>
                            <ActorChip actor={s.actor} />
                        </div>
                        <p className="text-[12.5px] text-[#4B5563] mt-1 leading-relaxed">{s.detail}</p>
                        {s.link && (
                            s.link.external ? (
                                <a href={s.link.to} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[12px] text-[#E04343] hover:underline mt-1.5 break-all">
                                    <Link2 className="w-3 h-3 shrink-0" /> {s.link.label}
                                </a>
                            ) : (
                                <NavLink to={s.link.to}
                                    className="inline-flex items-center gap-1 text-[12px] text-[#E04343] hover:underline mt-1.5">
                                    <Link2 className="w-3 h-3 shrink-0" /> {s.link.label}
                                </NavLink>
                            )
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ApiGatewayManual() {
    const [tab, setTab] = useState<'merchant' | 'partner'>('merchant');
    const [showDev, setShowDev] = useState(false);
    const [openSurface, setOpenSurface] = useState<string | null>(null);

    return (
        <>
            <AdminSidebar title="API Documentation" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Documentation" />
                <div className="lg:px-10 pb-14 px-6 bg-[#FAFAFA] min-h-screen">

                    <div className="pt-6 pb-4">
                        <h1 className="text-[20px] font-[700] text-[#333333] flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-[#E04343]" />
                            API Gateway — How It Works
                        </h1>
                    </div>

                    {/* Plain-English overview */}
                    <div className="bg-white rounded-[10px] p-5 shadow-sm border mb-5">
                        <h2 className="text-[15px] font-[700] text-[#333333] mb-2">What this is, in one paragraph</h2>
                        <p className="text-[13.5px] text-[#4B5563] leading-relaxed max-w-4xl">
                            Normally someone logs into Skynet and types in a shipment. The API lets another company's
                            software do that automatically instead — their website books a delivery with us the moment
                            their customer checks out, with nobody typing anything. This page explains how to get such a
                            company connected, and what to do when they say something is not working.
                        </p>
                        <p className="text-[13.5px] text-[#4B5563] leading-relaxed max-w-4xl mt-3">
                            <b>You do not need to be technical to run this.</b> Your part is approving who gets access,
                            agreeing what they pay, and sending them the right links. Their developer does the rest.
                        </p>
                    </div>

                    {/* Two kinds of integrator */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                        <div className="bg-white rounded-[10px] p-4 shadow-sm border-l-4 border-l-blue-500 border">
                            <p className="text-[14px] font-[700] text-[#333333] mb-1">Merchants &amp; customers</p>
                            <p className="text-[12.5px] text-[#4B5563] leading-relaxed">
                                Ordinary customers who want to book from their own online shop or system. They
                                <b> sign up themselves</b> on a web page you send them, and wait for you to approve.
                            </p>
                        </div>
                        <div className="bg-white rounded-[10px] p-4 shadow-sm border-l-4 border-l-[#E04343] border">
                            <p className="text-[14px] font-[700] text-[#333333] mb-1">Partners (e.g. NIPOST)</p>
                            <p className="text-[12.5px] text-[#4B5563] leading-relaxed">
                                Big companies handing us shipments in volume, usually billed monthly on account.
                                <b> You create these yourself</b> and agree rates before they start.
                            </p>
                        </div>
                    </div>

                    {/* Onboarding journeys */}
                    <div className="bg-white rounded-[10px] shadow-sm border mb-5">
                        <div className="p-4 border-b">
                            <h2 className="text-[15px] font-[700] text-[#333333] flex items-center gap-2">
                                <UserCheck className="w-4 h-4 text-[#E04343]" /> Getting someone connected, step by step
                            </h2>
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => setTab('merchant')}
                                    className={`text-[12.5px] font-[600] px-3 py-1.5 rounded-md ${tab === 'merchant' ? 'bg-[#E04343] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    A merchant / customer
                                </button>
                                <button onClick={() => setTab('partner')}
                                    className={`text-[12.5px] font-[600] px-3 py-1.5 rounded-md ${tab === 'partner' ? 'bg-[#E04343] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    A partner
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            <Journey steps={tab === 'merchant' ? MERCHANT_STEPS : PARTNER_STEPS} />
                        </div>
                    </div>

                    {/* Links to share */}
                    <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                        <h2 className="text-[15px] font-[700] text-[#333333] mb-1 flex items-center gap-2">
                            <Send className="w-4 h-4 text-[#E04343]" /> Links to send them
                        </h2>
                        <p className="text-[12.5px] text-[#808080] mb-3">
                            Copy these straight into an email. Nothing else needs explaining.
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12.5px]">
                                <thead className="bg-[#FAFAFA] text-[#808080] text-[10px] uppercase">
                                    <tr>
                                        <th className="text-left px-2 py-2 font-[600]">What it is</th>
                                        <th className="text-left px-2 py-2 font-[600]">Link</th>
                                        <th className="text-left px-2 py-2 font-[600]">When to send it</th>
                                        <th className="w-8"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {SHARE_LINKS.map((l) => (
                                        <tr key={l.label} className="border-t align-top">
                                            <td className="px-2 py-2.5 font-[600] text-[#19213D] whitespace-nowrap">{l.label}</td>
                                            <td className="px-2 py-2.5">
                                                <a href={l.url} target="_blank" rel="noopener noreferrer"
                                                    className="text-[#E04343] hover:underline break-all font-mono text-[11.5px]">
                                                    {l.url}
                                                </a>
                                            </td>
                                            <td className="px-2 py-2.5 text-[#4B5563]">
                                                {l.who}
                                                <span className="block text-[11px] text-[#808080] mt-0.5">{l.why}</span>
                                            </td>
                                            <td className="px-2 py-2.5"><CopyButton text={l.url} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Troubleshooting */}
                    <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                        <h2 className="text-[15px] font-[700] text-[#333333] mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" /> When they say it is not working
                        </h2>
                        <div className="space-y-3">
                            {TROUBLESHOOTING.map((t, i) => (
                                <div key={i} className="border rounded-[8px] p-3">
                                    <p className="text-[13px] font-[600] text-[#19213D]">“{t.symptom}”</p>
                                    <p className="text-[12.5px] text-[#808080] mt-1"><b>Usually means:</b> {t.cause}</p>
                                    <p className="text-[12.5px] text-[#4B5563] mt-0.5"><b>What to do:</b> {t.fix}</p>
                                </div>
                            ))}
                        </div>
                        <p className="text-[12.5px] text-[#4B5563] mt-3">
                            To see whether their system is reaching us at all, open{' '}
                            <NavLink to="/admin/api-gateway/analytics" className="text-[#E04343] hover:underline">Analytics</NavLink>{' '}
                            — every request they make appears there, successful or not.
                        </p>
                    </div>

                    {/* Glossary */}
                    <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                        <h2 className="text-[15px] font-[700] text-[#333333] mb-3 flex items-center gap-2">
                            <HelpCircle className="w-4 h-4 text-[#E04343]" /> Words you will hear
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            {GLOSSARY.map((g) => (
                                <div key={g.term}>
                                    <p className="text-[13px] font-[600] text-[#19213D]">{g.term}</p>
                                    <p className="text-[12.5px] text-[#4B5563] leading-relaxed">{g.means}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Developer reference — collapsed by default */}
                    <div className="bg-white rounded-[10px] shadow-sm border">
                        <button type="button" onClick={() => setShowDev((s) => !s)}
                            className="w-full flex items-center justify-between p-4 text-left">
                            <div className="flex items-center gap-2">
                                <Code2 className="w-4 h-4 text-[#808080]" />
                                <div>
                                    <p className="text-[15px] font-[700] text-[#333333]">Technical reference</p>
                                    <p className="text-[12px] text-[#808080]">
                                        For developers only — addresses, every endpoint, and the scheduled jobs.
                                    </p>
                                </div>
                            </div>
                            {showDev ? <ChevronDown className="w-4 h-4 text-[#808080]" /> : <ChevronRight className="w-4 h-4 text-[#808080]" />}
                        </button>

                        {showDev && (
                            <div className="px-4 pb-4 border-t pt-4">

                                {/* Base URLs */}
                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">Base URLs</p>
                                <div className="space-y-2 mb-5">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] font-[700] px-2 py-0.5 rounded-full bg-green-100 text-green-700">Production</span>
                                        <code className="text-[12.5px] bg-[#1F2937] text-white px-3 py-1.5 rounded font-mono break-all">{API_BASE}</code>
                                        <CopyButton text={API_BASE} />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] font-[700] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Local</span>
                                        <code className="text-[12.5px] bg-gray-100 px-3 py-1.5 rounded font-mono">http://127.0.0.1:8000/api/v1</code>
                                        <CopyButton text="http://127.0.0.1:8000/api/v1" />
                                    </div>
                                    <p className="text-[12px] text-[#808080]">
                                        All paths below are relative to the base — so{' '}
                                        <code className="bg-gray-100 px-1 rounded break-all">{API_BASE}/partner/shipments</code>{' '}
                                        creates a partner shipment. JSON in, JSON out.
                                    </p>
                                </div>

                                {/* Endpoints */}
                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">Endpoints</p>
                                <div className="space-y-2 mb-5">
                                    {SURFACES.map((s) => {
                                        const open = openSurface === s.id;
                                        return (
                                            <div key={s.id} className="border rounded-[8px]">
                                                <button type="button" onClick={() => setOpenSurface(open ? null : s.id)}
                                                    className="w-full flex flex-wrap items-center justify-between gap-2 p-3 text-left">
                                                    <span className="text-[13px] font-[600] text-[#19213D]">
                                                        {s.name} <code className="text-[11.5px] text-[#808080] font-mono">{s.prefix}/…</code>
                                                    </span>
                                                    <span className="text-[11px] text-[#808080]">
                                                        {s.auth} · {s.endpoints.length} endpoints · {open ? 'hide' : 'show'}
                                                    </span>
                                                </button>
                                                {open && (
                                                    <div className="px-3 pb-3 overflow-x-auto">
                                                        <table className="w-full text-[12px]">
                                                            <tbody>
                                                                {s.endpoints.map((e, i) => (
                                                                    <tr key={i} className="border-t">
                                                                        <td className="px-2 py-1.5 w-[70px]">
                                                                            <span className={`text-[10px] font-[700] px-1.5 py-0.5 rounded border ${methodColour(e.method)}`}>
                                                                                {e.method}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-2 py-1.5 font-mono text-[#333333] break-all">{s.prefix}{e.path}</td>
                                                                        <td className="px-2 py-1.5 text-[#808080]">{e.note || ''}</td>
                                                                        <td className="px-2 py-1.5 w-8"><CopyButton text={`${API_BASE}${s.prefix}${e.path}`} /></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Public + cron */}
                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">Public endpoints</p>
                                <ul className="text-[12px] text-[#4B5563] space-y-1 mb-5 list-disc ml-4">
                                    <li><code className="bg-gray-100 px-1 rounded break-all">{API_BASE}/health</code> — uptime monitoring, no auth</li>
                                    <li><code className="bg-gray-100 px-1 rounded break-all">{API_BASE}/docs</code> — Swagger UI</li>
                                    <li><code className="bg-gray-100 px-1 rounded break-all">{API_BASE}/docs/spec.json</code> — OpenAPI spec</li>
                                    <li><code className="bg-gray-100 px-1 rounded break-all">{API_BASE}/webhooks/nipost/callback</code> — inbound from NIPOST, signature verified</li>
                                </ul>

                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">Scheduled jobs</p>
                                <p className="text-[12px] text-[#808080] mb-2">
                                    Called over HTTP, protected by <code className="bg-gray-100 px-1 rounded">API_CRON_SECRET</code>.
                                    <b> If nothing schedules these, shipment statuses stop updating and partner invoices are never generated.</b>
                                </p>
                                <ul className="text-[12px] text-[#4B5563] space-y-1 mb-5 list-disc ml-4">
                                    {CRON_JOBS.map((j) => <li key={j}><code className="bg-gray-100 px-1 rounded">{j}</code></li>)}
                                </ul>

                                <p className="text-[11px] font-[600] text-[#808080] uppercase tracking-wide mb-2">Also on the server</p>
                                <ul className="text-[12px] text-[#4B5563] space-y-1 list-disc ml-4">
                                    <li>Postman collection — <code className="bg-gray-100 px-1 rounded">docs/Skynet_API_Gateway.postman_collection.json</code></li>
                                    <li>OpenAPI file — <code className="bg-gray-100 px-1 rounded">resources/api-docs/openapi.yaml</code></li>
                                    <li>Settings live in the server's environment file: NIPOST credentials, rate limits, webhook retries, log retention.</li>
                                </ul>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </>
    );
}

export default ApiGatewayManual;
